import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DownloadSimple, FileText, UploadSimple, Trash, Download, ShieldWarning, ArrowsClockwise, Package, EnvelopeSimple } from "@phosphor-icons/react";

async function downloadDoc(slug, filename) {
  // Force octet-stream type on the Blob so browser extensions
  // (Adobe Acrobat, Google Docs viewer) cannot intercept and render the file inline.
  // Cache-bust with a timestamp query param so the browser cannot serve a stale older version.
  try {
    const res = await api.get(`/docs/${slug}?t=${Date.now()}`, {
      responseType: "blob",
      headers: { "Cache-Control": "no-cache" },
    });
    const blob = new Blob([res.data], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a); a.click();
    setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 100);
  } catch (e) {
    alert(`Download failed: ${e.response?.status || ""} ${e.message}.\n\nIf this persists, your browser may have an extension (Adobe Acrobat, Office viewer) intercepting downloads. Try in an Incognito/Private window or disable the extension.`);
  }
}

async function downloadBundle() {
  try {
    const res = await api.get(`/docs/bundle.zip?t=${Date.now()}`, {
      responseType: "blob",
      headers: { "Cache-Control": "no-cache" },
    });
    const blob = new Blob([res.data], { type: "application/zip" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `SPAN_Docs_Bundle_${Date.now()}.zip`;
    a.style.display = "none";
    document.body.appendChild(a); a.click();
    setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 100);
  } catch (e) {
    alert("Bundle download failed: " + (e.response?.data?.detail || e.message));
  }
}

export default function Settings() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [docsManifest, setDocsManifest] = useState({});
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenResult, setRegenResult] = useState(null);
  const loadManifest = () =>
    api.get("/docs/manifest").then(({ data }) => setDocsManifest(data)).catch(() => {});
  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data));
    loadManifest();
  }, []);

  const isExec = me?.role === "CEO" || me?.role === "Admin" || me?.department === "Executive";
  const ugManifest = docsManifest["user-guide"] || {};
  const rcManifest = docsManifest["review-checklist"] || {};
  const bundleManifest = docsManifest["bundle"] || {};
  const fmtKB = (b) => b ? `${Math.round(b / 1024)} KB` : "";

  const regenerateAndEmail = async () => {
    if (!window.confirm("Regenerate both docs (~15s) and email them as attachments to all CEO/Admin/Executive users?")) return;
    setRegenBusy(true); setRegenResult(null);
    try {
      const { data } = await api.post("/admin/docs/regenerate-and-email");
      setRegenResult(data);
      await loadManifest();
    } catch (e) {
      setRegenResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setRegenBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <div className="label-eyebrow">Administration</div>
        <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Users & RACI</h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1">9 departments · role-based access</div>
      </div>

      <div className="card-flat p-5 flex flex-wrap items-center justify-between gap-4" data-testid="user-guide-card">
        <div className="flex items-start gap-3">
          <FileText size={22} weight="duotone" />
          <div>
            <div className="font-display font-bold text-[15px] flex items-center gap-2">
              SPAN Complete User Guide
              {ugManifest.version && <span className="badge badge-info text-[9px]" data-testid="user-guide-version">{ugManifest.version}</span>}
            </div>
            <div className="text-[12px] text-[var(--span-text-2)] mt-0.5">
              Every feature, every workflow — 34 parts + 4 appendices including the P5 CEO Strategic Answers. Microsoft Word (.docx) format.
              {ugManifest.size_bytes ? <span className="ml-2 font-mono text-[10px]">· {fmtKB(ugManifest.size_bytes)}</span> : null}
            </div>
          </div>
        </div>
        <button
          data-testid="download-user-guide-btn"
          onClick={() => downloadDoc("user-guide", ugManifest.filename || "SPAN_User_Guide.docx")}
          className="span-btn"
        >
          <DownloadSimple size={14} /> Download {ugManifest.version || ""} .docx
        </button>
      </div>

      <div className="card-flat p-5 flex flex-wrap items-center justify-between gap-4" data-testid="review-checklist-card">
        <div className="flex items-start gap-3">
          <FileText size={22} weight="duotone" />
          <div>
            <div className="font-display font-bold text-[15px] flex items-center gap-2">
              Feature Review Checklist
              {rcManifest.version && <span className="badge badge-info text-[9px]">{rcManifest.version}</span>}
            </div>
            <div className="text-[12px] text-[var(--span-text-2)] mt-0.5">
              Section-wise checklist with checkboxes &amp; notes columns — mark what works,
              what needs change, and what's missing. Send back to apply changes.
              {rcManifest.size_bytes ? <span className="ml-2 font-mono text-[10px]">· {fmtKB(rcManifest.size_bytes)}</span> : null}
            </div>
          </div>
        </div>
        <button
          data-testid="download-review-checklist-btn"
          onClick={() => downloadDoc("review-checklist", rcManifest.filename || "SPAN_Review_Checklist.docx")}
          className="span-btn"
        >
          <DownloadSimple size={14} /> Download {rcManifest.version || ""} .docx
        </button>
      </div>

      <div className="card-flat p-5 flex flex-wrap items-center justify-between gap-4" data-testid="docs-bundle-card">
        <div className="flex items-start gap-3">
          <Package size={22} weight="duotone" />
          <div>
            <div className="font-display font-bold text-[15px]">Download both as a ZIP</div>
            <div className="text-[12px] text-[var(--span-text-2)] mt-0.5">
              One archive containing the user guide and the review checklist at their latest versions.
              {bundleManifest.size_bytes ? <span className="ml-2 font-mono text-[10px]">· ~{fmtKB(bundleManifest.size_bytes)} uncompressed</span> : null}
            </div>
          </div>
        </div>
        <button data-testid="download-bundle-btn" onClick={downloadBundle} className="span-btn">
          <Download size={14} /> Download bundle (.zip)
        </button>
      </div>

      {isExec && (
        <div className="card-flat p-5" data-testid="docs-regen-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <EnvelopeSimple size={22} weight="duotone" />
              <div>
                <div className="font-display font-bold text-[15px]">Regenerate + email me the latest docs</div>
                <div className="text-[12px] text-[var(--span-text-2)] mt-0.5">
                  Runs the build scripts (~15s), then emails both .docx files as attachments to every
                  CEO / Admin / Executive account. Screenshots are not re-captured — they're refreshed
                  separately when UI changes.
                </div>
              </div>
            </div>
            <button data-testid="regen-docs-btn" onClick={regenerateAndEmail} disabled={regenBusy}
                    className="span-btn">
              <ArrowsClockwise size={14} className={regenBusy ? "animate-spin" : ""}/>
              {regenBusy ? "Regenerating…" : "Regenerate + email me"}
            </button>
          </div>
          {regenResult && (
            <div className="mt-4 text-[12px] hairline surface p-3 font-mono" data-testid="regen-result">
              {regenResult.error && (
                <span className="text-red-600">Error: {String(regenResult.error)}</span>
              )}
              {!regenResult.error && (
                <>
                  <div className="text-emerald-700 mb-1">
                    ✓ Rebuilt {Object.keys(regenResult.manifest || {}).length} docs · emailed {regenResult.emails_sent || 0} / {regenResult.audience?.length || 0} recipients
                    {!regenResult.resend_available && <span className="ml-2 text-amber-600">(Resend not configured — docs ready but no email sent)</span>}
                  </div>
                  {regenResult.audience?.length > 0 && (
                    <div className="text-[var(--span-text-2)] text-[10px] mt-1">
                      → {regenResult.audience.join(", ")}
                    </div>
                  )}
                  {regenResult.email_failures?.length > 0 && (
                    <div className="text-amber-700 text-[10px] mt-2">
                      <div className="font-bold">Email delivery failed for {regenResult.email_failures.length} recipient{regenResult.email_failures.length === 1 ? "" : "s"}:</div>
                      <ul className="ml-3 list-disc mt-1">
                        {regenResult.email_failures.map((f, i) => (
                          <li key={i}><b>{f.email}</b> — {f.error}</li>
                        ))}
                      </ul>
                      <div className="text-[var(--span-text-2)] mt-1.5">
                        Tip: this is typically a Resend "domain not verified" error. Complete the SPF/DKIM/DMARC DNS step (CEO-6) and the next regen will deliver. In the meantime, download the docs directly:
                      </div>
                      <div className="mt-1.5 flex gap-2 flex-wrap">
                        <button onClick={() => downloadDoc("user-guide", "SPAN_User_Guide.docx")}
                                className="text-[10px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]">Download user guide</button>
                        <button onClick={() => downloadDoc("review-checklist", "SPAN_Review_Checklist.docx")}
                                className="text-[10px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]">Download checklist</button>
                        <button onClick={downloadBundle}
                                className="text-[10px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]">Download bundle (.zip)</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="card-flat">
        <table className="span-table">
          <thead><tr><th>Name</th><th>Email</th><th>Department</th><th>Role</th><th>UX tier</th><th>Auth</th><th>Created</th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.user_id}>
                <td className="font-medium">{u.name}</td>
                <td className="font-mono text-[12px] text-[var(--span-text-2)]">{u.email}</td>
                <td><span className="badge">{u.department}</span></td>
                <td>{u.role}</td>
                <td>
                  {(me?.role === "CEO" || me?.department === "Executive" || me?.department === "Admin") ? (
                    <select
                      data-testid={`ux-tier-select-${u.user_id}`}
                      defaultValue={u.ux_tier || 2}
                      onChange={async (e) => {
                        const tier = Number(e.target.value);
                        try {
                          await api.put(`/admin/users/${u.user_id}/ux-tier`, { ux_tier: tier });
                          const { data } = await api.get("/users");
                          setUsers(data);
                        } catch (err) {
                          alert(err.response?.data?.detail || "Failed to update ux_tier");
                        }
                      }}
                      className="hairline bg-transparent text-[11px] font-mono py-0.5 px-1"
                    >
                      <option value={1}>1 · Field</option>
                      <option value={2}>2 · Case</option>
                      <option value={3}>3 · Head</option>
                      <option value={4}>4 · Exec</option>
                    </select>
                  ) : (
                    <span className="font-mono text-[11px] text-[var(--span-text-2)]">{u.ux_tier || 2}</span>
                  )}
                </td>
                <td><span className={`badge ${u.auth_provider === "google" ? "badge-info" : ""}`}>{u.auth_provider}</span></td>
                <td className="text-[var(--span-text-2)]">{fmtDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ImportWizard />
      <GdprPanel />
      <IntegrationsPanel />
      <NforRoutingPanel users={users} />
      <BlockchainPanel />
      <SandboxPanel currentUser={me} />
    </div>
  );
}

function NforRoutingPanel({ users }) {
  const [routing, setRouting] = useState({ finance_head_user_id:"", mentor_user_id:"", coo_user_id:"", ceo_user_id:"" });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/org-settings/nfor-routing").then(r => {
      setRouting({
        finance_head_user_id: r.data?.finance_head_user_id?.user_id || "",
        mentor_user_id: r.data?.mentor_user_id?.user_id || "",
        coo_user_id: r.data?.coo_user_id?.user_id || "",
        ceo_user_id: r.data?.ceo_user_id?.user_id || "",
      });
    });
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/org-settings/nfor-routing", routing);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } finally { setBusy(false); }
  };
  const opts = (role) => (
    <select value={routing[role]} onChange={e => setRouting({ ...routing, [role]: e.target.value })}
            data-testid={`nfor-routing-${role}`}
            className="w-full hairline px-2 py-1.5 text-[12px]">
      <option value="">— Use RBAC fallback —</option>
      {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.department} · {u.role}</option>)}
    </select>
  );
  return (
    <div className="card-flat p-5" data-testid="nfor-routing-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="label-eyebrow">NFOR alert routing</div>
          <div className="font-display font-bold text-[15px] mt-1">4-way stakeholder routing</div>
          <div className="text-[12px] text-[var(--span-text-2)]">Pin a specific user for each role — used by NFOR submission alerts. Leave blank to fall back to RBAC (e.g. all Finance Managers).</div>
        </div>
        <button data-testid="nfor-routing-save" disabled={busy} onClick={save} className="span-btn">{busy ? "Saving…" : (saved ? "✓ Saved" : "Save")}</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div><label className="label-eyebrow block mb-1">Finance Head</label>{opts("finance_head_user_id")}</div>
        <div><label className="label-eyebrow block mb-1">Mentor Director</label>{opts("mentor_user_id")}</div>
        <div><label className="label-eyebrow block mb-1">COO</label>{opts("coo_user_id")}</div>
        <div><label className="label-eyebrow block mb-1">CEO</label>{opts("ceo_user_id")}</div>
      </div>
    </div>
  );
}

function IntegrationsPanel() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ slack_webhook_url: "", teams_webhook_url: "", discord_webhook_url: "", whatsapp_number: "" });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/org/integrations");
    setInfo(data);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/org/integrations", {
        slack_webhook_url: form.slack_webhook_url || null,
        teams_webhook_url: form.teams_webhook_url || null,
        discord_webhook_url: form.discord_webhook_url || null,
        whatsapp_number: form.whatsapp_number || null,
      });
      setForm({ slack_webhook_url: "", teams_webhook_url: "", discord_webhook_url: "", whatsapp_number: "" });
      load();
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/org/integrations/test");
      alert("Test card dispatched to every configured webhook. Check your Slack/Teams/Discord workspace.");
    } catch (e) {
      alert("Test failed: " + (e.response?.data?.detail || e.message));
    } finally { setTesting(false); }
  };

  if (!info) return null;

  return (
    <div className="card-flat p-5 space-y-4" data-testid="integrations-panel">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display font-bold text-[15px]">Integrations</div>
          <div className="text-[12px] text-[var(--span-text-2)] mt-0.5">
            Slack · Teams · Discord webhooks fan out every notification SPAN sends.
            WhatsApp number is for display only; Twilio routing happens at the org level via env.
          </div>
        </div>
        <button data-testid="integrations-test-btn" onClick={sendTest} disabled={testing || (!info.slack_configured && !info.teams_configured && !info.discord_configured)} className="span-btn span-btn-ghost h-8 text-[11px]">
          {testing ? "Sending…" : "Send test card"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Status label="Slack" ok={info.slack_configured} preview={info.slack_preview}/>
        <Status label="Teams" ok={info.teams_configured} preview={info.teams_preview}/>
        <Status label="Discord" ok={info.discord_configured} preview={info.discord_preview}/>
      </div>

      <form onSubmit={save} className="space-y-3 pt-2 border-t border-[var(--span-border)]">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--span-text-2)]">Update webhook URLs (leave blank to keep current)</div>
        <Field id="slack" label="Slack incoming webhook URL"
               placeholder="https://hooks.slack.com/services/T.../B.../..."
               value={form.slack_webhook_url} onChange={(v) => set("slack_webhook_url", v)}/>
        <Field id="teams" label="Microsoft Teams incoming webhook URL"
               placeholder="https://outlook.office.com/webhook/..."
               value={form.teams_webhook_url} onChange={(v) => set("teams_webhook_url", v)}/>
        <Field id="discord" label="Discord webhook URL"
               placeholder="https://discord.com/api/webhooks/..."
               value={form.discord_webhook_url} onChange={(v) => set("discord_webhook_url", v)}/>
        <Field id="whatsapp" label="WhatsApp business number (display only)"
               placeholder="+91 99999 99999"
               value={form.whatsapp_number} onChange={(v) => set("whatsapp_number", v)}/>
        <div className="flex justify-end">
          <button disabled={busy} className="span-btn" data-testid="save-integrations-btn">Save webhooks</button>
        </div>
      </form>
    </div>
  );
}

function Status({ label, ok, preview }) {
  return (
    <div className={`hairline px-3 py-2 surface text-[12px] flex items-center justify-between ${ok ? "" : "opacity-60"}`}>
      <span><span className={`inline-block w-1.5 h-1.5 mr-2 rounded-full ${ok ? "bg-green-500" : "bg-gray-400"}`}/>{label}</span>
      <span className="text-[10px] font-mono text-[var(--span-text-2)]">{ok ? preview : "not configured"}</span>
    </div>
  );
}

function Field({ id, label, placeholder, value, onChange }) {
  return (
    <div>
      <label className="text-[11px] block mb-1">{label}</label>
      <input data-testid={`integration-${id}`} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="span-input font-mono text-[12px]"/>
    </div>
  );
}

function BlockchainPanel() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/blockchain/status").then(({ data }) => setInfo(data)).catch(() => setInfo({}));
  }, []);
  if (!info) return null;
  return (
    <div className="card-flat p-5" data-testid="blockchain-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-[15px]">Polygon anchoring</div>
          <div className="text-[12px] text-[var(--span-text-2)] mt-0.5">
            Mode: <span className="font-mono">{info.mode || "simulated"}</span> · Network: <span className="font-mono">{info.network || "—"}</span>
          </div>
          {info.wallet && (
            <div className="text-[11px] font-mono text-[var(--span-text-2)] mt-2">
              Wallet: {info.wallet} · Balance: {(Number(info.balance_wei || 0) / 1e18).toFixed(6)} MATIC · Block #{info.block_number}
            </div>
          )}
          {info.mode === "simulated" && (
            <div className="text-[11px] text-[var(--span-text-2)] mt-2 max-w-prose">
              Set <span className="font-mono">POLYGON_RPC_URL</span> and <span className="font-mono">POLYGON_PRIVATE_KEY</span> in the
              backend environment to activate real on-chain anchoring. Recommended: Polygon Amoy testnet RPC
              (<a href="https://rpc-amoy.polygon.technology" target="_blank" rel="noreferrer" className="underline">rpc-amoy.polygon.technology</a>)
              with a funded test wallet.
            </div>
          )}
          {info.error && (
            <div className="text-[11px] text-[var(--span-danger)] font-mono mt-2">RPC error: {info.error}</div>
          )}
        </div>
        <span className={`badge ${info.mode === "real" ? "badge-success" : ""}`}>{info.mode || "simulated"}</span>
      </div>
    </div>
  );
}

function SandboxPanel({ currentUser }) {
  const [busy, setBusy] = useState(false);
  const isAdmin = currentUser && (
    currentUser.role === "CEO" || currentUser.role === "Admin" || currentUser.department === "Executive"
  );
  if (!isAdmin) return null;
  const reset = async () => {
    if (!window.confirm("Reset the sandbox tenant? This wipes demo cases/parties and re-seeds the showcase dataset. Admin-only.")) return;
    setBusy(true);
    try {
      await api.post("/admin/sandbox/reset");
      alert("Sandbox reset complete. Reload pages to see fresh demo data.");
    } catch (e) {
      alert("Reset failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <div className="card-flat p-5 flex items-center justify-between" data-testid="sandbox-panel">
      <div>
        <div className="font-display font-bold text-[15px]">Sandbox / demo tenant</div>
        <div className="text-[12px] text-[var(--span-text-2)] mt-0.5">Wipe demo data and re-seed the showcase dataset (admin-only).</div>
      </div>
      <button data-testid="sandbox-reset-btn" onClick={reset} disabled={busy} className="span-btn span-btn-ghost">
        Reset sandbox
      </button>
    </div>
  );
}

function ImportWizard() {
  const [kind, setKind] = useState("cases");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [busy, setBusy] = useState(false);

  const previewFn = async () => {
    if (!file) return;
    setBusy(true); setCommitted(null);
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post(`/import/${kind}/preview`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) {
      alert("Preview failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file) return;
    if (!window.confirm(`Import ${preview?.row_count || "?"} rows into ${kind}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post(`/import/${kind}/commit`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setCommitted(data); setPreview(null); setFile(null);
    } catch (e) {
      alert("Import failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    const { data } = await api.get(`/import/${kind}/template`);
    const blob = new Blob([data.csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${kind}_template.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="card-flat p-5 space-y-4" data-testid="import-wizard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <UploadSimple size={22} weight="duotone" />
          <div>
            <div className="font-display font-bold text-[15px]">CSV Import Wizard</div>
            <div className="text-[12px] text-[var(--span-text-2)] mt-0.5">
              Bulk-import cases or parties from a legacy CRM. Preview before commit.
            </div>
          </div>
        </div>
        <button data-testid="download-template-btn" onClick={downloadTemplate} className="span-btn span-btn-ghost h-8 text-[11px]">
          <Download size={12}/> {kind} template
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label-eyebrow block mb-1">What are you importing?</label>
          <select value={kind} onChange={(e) => { setKind(e.target.value); setPreview(null); setCommitted(null); }} data-testid="import-kind" className="span-input">
            <option value="cases">Cases</option>
            <option value="parties">Parties</option>
          </select>
        </div>
        <div className="flex-1 min-w-[260px]">
          <label className="label-eyebrow block mb-1">CSV file</label>
          <input type="file" accept=".csv" onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); }} className="span-input" data-testid="import-file"/>
        </div>
        <button disabled={!file || busy} onClick={previewFn} className="span-btn span-btn-ghost" data-testid="import-preview-btn">
          Preview
        </button>
        <button disabled={!preview || busy || preview.row_count === 0} onClick={commit} className="span-btn" data-testid="import-commit-btn">
          Commit import
        </button>
      </div>

      {preview && (
        <div className="border border-[var(--span-border)] p-3 bg-[var(--span-surface-2)]" data-testid="import-preview">
          <div className="text-[12px] font-mono mb-2">
            ✓ {preview.row_count} valid rows · {preview.errors.length} errors
          </div>
          {preview.errors.length > 0 && (
            <ul className="text-[11px] text-[var(--span-danger)] mb-2 font-mono">
              {preview.errors.slice(0, 6).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
          {preview.sample.length > 0 && (
            <div className="overflow-auto">
              <table className="span-table text-[11px]">
                <thead><tr>{(preview.headers || Object.keys(preview.sample[0])).map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {preview.sample.map((r, i) => (
                    <tr key={i}>{(preview.headers || Object.keys(r)).map((h) => <td key={h} className="font-mono text-[11px]">{r[h] || "—"}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {committed && (
        <div className="border-l-4 border-[var(--span-success)] bg-green-50 dark:bg-green-950 p-3 text-[12px]" data-testid="import-committed">
          Imported <span className="font-bold">{committed.inserted}</span> {kind}.
          {committed.failed.length > 0 && <> {committed.failed.length} rows failed.</>}
        </div>
      )}
    </div>
  );
}

function GdprPanel() {
  const [busy, setBusy] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [confirm, setConfirm] = useState("");

  const exportMe = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `span_my_data_${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const erase = async () => {
    if (confirm !== "DELETE") return;
    setBusy(true);
    try {
      await api.post("/me/erase", { confirm: "DELETE" });
      alert("Your account has been erased. You will be signed out.");
      window.location.href = "/login";
    } catch (e) {
      alert("Erase failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-5 space-y-3" data-testid="gdpr-panel">
      <div className="flex items-center gap-3">
        <ShieldWarning size={22} weight="duotone" />
        <div>
          <div className="font-display font-bold text-[15px]">Your data (GDPR)</div>
          <div className="text-[12px] text-[var(--span-text-2)] mt-0.5">
            Export everything SPAN holds about you, or erase your personal data. Case files remain operational records.
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <button data-testid="gdpr-export-btn" disabled={busy} onClick={exportMe} className="span-btn">
          <Download size={14}/> Export my data (JSON)
        </button>
        <button data-testid="gdpr-erase-toggle" onClick={() => setErasing((x) => !x)} className="span-btn span-btn-ghost">
          <Trash size={14}/> Erase my data
        </button>
      </div>
      {erasing && (
        <div className="border border-[var(--span-danger)] p-3 bg-red-50 dark:bg-red-950" data-testid="gdpr-erase-confirm">
          <div className="text-[12px] mb-2 text-[var(--span-danger)]">
            This will anonymize your account, redact your name and email from trails, and revoke all sessions. Type <span className="font-mono font-bold">DELETE</span> to confirm.
          </div>
          <div className="flex gap-2">
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="span-input flex-1" data-testid="gdpr-confirm-input" />
            <button data-testid="gdpr-erase-confirm-btn" disabled={confirm !== "DELETE" || busy} onClick={erase} className="span-btn">Erase</button>
          </div>
        </div>
      )}
    </div>
  );
}
